<template>
    <div>
        <div class="d-flex align-items-center justify-content-between mb-2">
            <label class="font-weight-bold mb-0">버전 타임라인</label>
            <b-button
                size="sm"
                variant="outline-primary"
                :disabled="adding || loading || !!error || !versions.length"
                :title="versions.length ? `최신 저장본(v${versions[0].version})을 복제해 다음 번호의 버전을 만듭니다.` : ''"
                @click="addNewVersion"
            >
                <b-spinner v-if="adding" small class="mr-1" />
                새 버전 추가
            </b-button>
        </div>
        <div v-if="loading" class="text-center py-3">
            <b-spinner variant="primary" />
        </div>
        <!-- 조회 실패를 '버전 없음'으로 보이면 잘못된 번호로 새 버전을 만들다 중복 오류가 난다 -->
        <div v-else-if="error" class="text-center py-3">
            <div class="text-danger small mb-2">{{ error }}</div>
            <b-button size="sm" variant="outline-secondary" @click="fetchVersions">다시 시도</b-button>
        </div>
        <div v-else-if="versions.length === 0" class="text-muted text-center py-3">
            <small>버전이 없습니다.</small>
        </div>
        <div v-else class="version-list">
            <div v-if="truncated" class="text-muted small mb-1">검색 결과가 많아(최근 {{ FETCH_SIZE }}개까지만 조회) 일부 버전이 빠졌을 수 있습니다.</div>
            <div
                v-for="v in versions"
                :key="v.id || v.version"
                :class="['version-item', 'p-2', 'mb-1', 'border', 'rounded', { 'border-primary bg-light': currentVersion === v.version }]"
                style="cursor: pointer;"
                @click="$emit('version-selected', v)"
            >
                <div class="d-flex align-items-center justify-content-between">
                    <span class="font-weight-bold">v{{ v.version }}</span>
                    <b-badge :variant="v.is_active ? 'success' : 'secondary'">
                        {{ v.is_active ? '활성' : '비활성' }}
                    </b-badge>
                </div>
                <div class="text-muted small mt-1">{{ v.label }}</div>
                <div class="text-muted" style="font-size: 0.75rem;">{{ formatDate(v.created_at) }}</div>
            </div>
        </div>
    </div>
</template>

<script>
import dayjs from 'dayjs';
import { getErrorMessage } from '@core/utils/api/errorMessage';

// 같은 코드의 버전을 keyword 검색으로 모은다(코드 부분 일치라 다른 코드도 섞여 온다)
const FETCH_SIZE = 100;

export default {
    name: 'SnippetVersionTimeline',
    props: {
        snippetCode: {
            type: String,
            default: ''
        },
        currentVersion: {
            type: Number,
            default: null
        }
    },
    data() {
        return {
            FETCH_SIZE,
            versions: [],
            loading: false,
            error: '',
            truncated: false,
            fetchSeq: 0,
            adding: false,
        };
    },
    watch: {
        snippetCode: {
            immediate: true,
            handler(val) {
                if (val) this.fetchVersions();
            }
        }
    },
    methods: {
        async fetchVersions() {
            if (!this.snippetCode) return;
            const seq = ++this.fetchSeq;
            const code = this.snippetCode;
            this.loading = true;
            this.error = '';
            try {
                const resp = await this.$axios.get('/mail/snippet', { params: { keyword: code, size: FETCH_SIZE } });
                if (seq !== this.fetchSeq) return;
                const items = resp.data.results || resp.data.items || resp.data;
                this.truncated = (resp.data.total || 0) > items.length;
                this.versions = items
                    .filter(v => v.code === code)
                    .sort((a, b) => b.version - a.version);
            } catch (e) {
                if (seq !== this.fetchSeq || e.response?.status === 401) return;
                this.versions = [];
                this.error = getErrorMessage(e, '버전을 불러오지 못했습니다.');
            } finally {
                if (seq === this.fetchSeq) this.loading = false;
            }
        },
        async addNewVersion() {
            if (!this.versions.length || this.error) return;
            const code = this.snippetCode;
            let next = null;
            this.adding = true;
            try {
                // 버전 없이 조회하면 서버가 가장 높은 버전을 준다 — 목록(부분 일치·100개 제한)의 첫 행보다 확실하다.
                // 숫자뿐인 코드는 /mail/snippet/<숫자> 가 ID 조회로 잡히므로 목록의 최신 버전을 명시해 조회한다.
                const url = /^\d+$/.test(code)
                    ? `/mail/snippet/${code}/${this.versions[0].version}`
                    : `/mail/snippet/${encodeURIComponent(code)}`;
                const { data: latest } = await this.$axios.get(url);
                next = latest.version + 1;
                await this.$axios.post('/mail/snippet', {
                    code,
                    version: next,
                    label: `v${next}`,
                    content: latest.content || '',
                });
                // 부모 목록도 새 버전을 보이게
                this.$emit('version-added', { code, version: next });
                await this.fetchVersions();
                this.$EventBus.$emit('toast-success', '성공', `v${next} 버전이 추가되었습니다.`);
            } catch (e) {
                const status = e.response?.status;
                if (status === 401) return;
                if (status === 409) {
                    // 그 사이 다른 곳에서 같은 번호를 만들었다 — 목록을 다시 받아 다시 시도할 수 있게
                    this.$EventBus.$emit('toast-failure', '오류', `v${next} 버전이 이미 있습니다. 버전 목록을 다시 불러왔습니다.`);
                    this.fetchVersions();
                    return;
                }
                this.$EventBus.$emit('toast-failure', '오류', getErrorMessage(e, '버전 추가에 실패했습니다.'));
            } finally {
                this.adding = false;
            }
        },
        formatDate(date) {
            if (!date) return '';
            return dayjs(date).format('YYYY-MM-DD HH:mm');
        }
    }
};
</script>

<style scoped>
.version-item:hover {
    background-color: #f8f9fa;
}
</style>
